import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocDivider } from 'commons/components/components-wrapper/poc-divider.component';
import { PocHeaderRow } from 'commons/components/components-wrapper/poc-header-row.component';
import { PocLogo } from 'commons/components/components-wrapper/poc-logo.component';
import { TooltipedIconComponent } from 'commons/components';
import { LanguagePicker } from 'commons/components/language-picker/language-picker.component';
import { useAuthContext } from 'core/auth/hooks/use-auth-context.hook';
import { appRoles } from 'core/constants/constants';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { useTranslation } from 'core/i18n/i18n';
import { User } from 'oidc-react';
import { HeaderOptions, PocHeaderStyled, SettingsOptions, UserOptions } from './app-header.style';
import { ProductInfoComponent } from './product-info/product-info.component';

export interface HeaderAppProps {
    userInSession?: User | null;
    languages: { text: string; value: string }[];
    onOpenSettings: VoidFunction;
    onOpenAboutBox: VoidFunction;
    onLogout: VoidFunction;
    onOpenUserAssistance: VoidFunction;
    isCheckingUserAssistanceAvailability: boolean;
    isUserAssistanceAvailable: boolean;
    userAssistanceTooltipKey: string;
}

export const HeaderApp: React.FC<HeaderAppProps> = (props) => {
    const {
        userInSession,
        languages,
        onOpenSettings,
        onOpenAboutBox,
        onLogout,
        onOpenUserAssistance,
        isCheckingUserAssistanceAvailability,
        isUserAssistanceAvailable,
        userAssistanceTooltipKey,
    } = props;
    const { userLanguage, updateUserLanguage } = useUserPreferencesContext();
    const { userRole } = useAuthContext();

    const { t } = useTranslation();

    return (
        <PocHeaderStyled>
            <PocHeaderRow elevated separator variant="primary">
                <ProductInfoComponent />
                <HeaderOptions>
                    <SettingsOptions>
                        <TooltipedIconComponent
                            icon="circle_help"
                            tooltipText={t(userAssistanceTooltipKey)}
                            ariaLabel={t(userAssistanceTooltipKey)}
                            family="outlined"
                            onClick={onOpenUserAssistance}
                            isLoading={isCheckingUserAssistanceAvailability}
                            disabled={!isUserAssistanceAvailable}
                            dataTestId="header-help"
                        />
                        <TooltipedIconComponent
                            icon="circle_info"
                            tooltipText={t('app.header.options.about')}
                            family="outlined"
                            onClick={onOpenAboutBox}
                            dataTestId="header-about"
                        />
                        {userRole === appRoles.POCC_ADMIN ? (
                            <TooltipedIconComponent
                                icon="settings"
                                tooltipText={t('settings.title')}
                                family="outlined"
                                onClick={onOpenSettings}
                                dataTestId="header-settings"
                            />
                        ) : undefined}
                        <LanguagePicker
                            languageSelected={userLanguage}
                            languagesOptions={languages}
                            onChangeLanguage={(languageSelected: string) => updateUserLanguage(languageSelected)}
                        />
                    </SettingsOptions>
                    <PocDivider direction="vertical" />
                    <UserOptions>
                        {userInSession ? (
                            <PocButton
                                id="logout-button"
                                data-testid="logout-button"
                                elevated
                                usage={'default'}
                                variant={'tertiary'}
                                type="button"
                                onClick={onLogout}
                            >
                                {t('app.header.options.logout')}
                            </PocButton>
                        ) : undefined}
                        <PocLogo name="roche_blue" size="alternate" data-testid="header-logo-roche" />
                    </UserOptions>
                </HeaderOptions>
            </PocHeaderRow>
        </PocHeaderStyled>
    );
};
